import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { setAccessToken } from '@/services/client'
import { apiClient } from '@/services/client'
import * as authService from '@/services/auth.service'

export type UserRole = 'GESTIONNAIRE' | 'AGENT_PMI' | 'RESPONSABLE_PMI' | 'ADMIN'

export interface AuthUser {
  id: string
  email: string
  nom: string
  prenom: string
  role: UserRole
  structures: Array<{ id: string; nom: string }>
  departements: Array<{ id: string; code: string; nom: string }>
  subscription: { plan: string; status: string } | null
  isOnboarded?: boolean
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null)
  const loading = ref(false)
  const initialized = ref(false)

  const isAuthenticated = computed(() => !!user.value)
  const isAdmin = computed(() => user.value?.role === 'ADMIN')
  const isGestionnaire = computed(() => user.value?.role === 'GESTIONNAIRE')
  const isAgentPmi = computed(() => user.value?.role === 'AGENT_PMI')
  const isResponsablePmi = computed(() => user.value?.role === 'RESPONSABLE_PMI')
  const isPmi = computed(() => isAgentPmi.value || isResponsablePmi.value)
  const fullName = computed(() => user.value ? `${user.value.prenom} ${user.value.nom}` : '')

  function hasRole(roles: UserRole[]): boolean {
    return !!user.value && roles.includes(user.value.role)
  }

  async function fetchUser(): Promise<void> {
    try {
      const res = await apiClient.get('/auth/me')
      user.value = res.data.data ?? res.data
    } catch {
      user.value = null
    }
  }

  async function login(): Promise<void> {
    await authService.initiateLogin()
  }

  async function handleCallback(code: string, state: string): Promise<void> {
    loading.value = true
    try {
      const result = await authService.handleCallback(code, state)
      setAccessToken(result.accessToken)
      await fetchUser()
    } finally {
      loading.value = false
    }
  }

  async function tryRefresh(): Promise<boolean> {
    try {
      const result = await authService.refreshToken()
      setAccessToken(result.accessToken)
      await fetchUser()
      return true
    } catch {
      user.value = null
      setAccessToken(null)
      return false
    }
  }

  async function initialize(): Promise<void> {
    if (initialized.value) return
    loading.value = true
    try {
      await tryRefresh()
    } finally {
      loading.value = false
      initialized.value = true
    }
  }

  async function logout(): Promise<void> {
    try {
      await authService.logout()
    } finally {
      user.value = null
      setAccessToken(null)
    }
  }

  return {
    user,
    loading,
    initialized,
    isAuthenticated,
    isAdmin,
    isGestionnaire,
    isAgentPmi,
    isResponsablePmi,
    isPmi,
    fullName,
    hasRole,
    fetchUser,
    login,
    handleCallback,
    tryRefresh,
    initialize,
    logout,
  }
})
